import type { AxiosInstance } from 'axios';
/**
 * Agent task lifecycle service.
 *
 * Wraps the previously-orphan POST / PATCH /api/v1/tasks endpoints.
 * Read-side (lists + counts) lives in workQueue.service.ts; this
 * module owns the write-side only — create new manual tasks +
 * mutate existing ones (mark complete, snooze, reassign).
 *
 * RBAC is enforced server-side in tasks_service.py:
 *   · Agents create tasks assigned to themselves; can only mutate
 *     tasks where assigned_to = self.
 *   · Managers / super_admin create + reassign across the team.
 */
import authService from './auth.service';

export interface TaskCreateBody {
  title: string;
  description?: string | null;
  /** Default 'manual' — auto-generated kinds (commitment_overdue,
   *  commitment_renewal, kyc_expiring) come from background jobs. */
  kind?: 'manual' | 'commitment_overdue' | 'commitment_renewal' | 'kyc_expiring';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  /** Defaults to the calling agent. Managers can target any agent. */
  assigned_to?: string | null;
  client_id?: string | null;
  commitment_id?: string | null;
  /** YYYY-MM-DD; null means open-ended (no due date). */
  due_date?: string | null;
}

export interface TaskUpdateBody {
  title?: string;
  description?: string | null;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  /** Terminal values 'done' / 'cancelled' cannot be reopened. */
  status?: 'pending' | 'done' | 'cancelled';
  due_date?: string | null;
  assigned_to?: string | null;
}

class TasksService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async createTask(body: TaskCreateBody): Promise<{ task_id: string }> {
    const res = await this.api.post<{ task_id: string }>('/api/v1/tasks', body);
    return res.data;
  }

  /** PATCH /tasks/{id} — server returns 204 No Content on success.
   *  Caller decides whether to refetch the parent list or update
   *  optimistically (the work-queue page refetches; the client-detail
   *  page can just remove the row). */
  async updateTask(taskId: string, body: TaskUpdateBody): Promise<void> {
    await this.api.patch(`/api/v1/tasks/${taskId}`, body);
  }

  /** Convenience wrapper — most common write from the dashboard. */
  async markComplete(taskId: string): Promise<void> {
    return this.updateTask(taskId, { status: 'done' });
  }
}

export default new TasksService();
